import React, { useState, useEffect, useRef } from "react";
import { MarketingLayout } from "@/components/marketing/MarketingLayout";
import { PageHero } from "@/components/marketing/blocks";
import { useSeo } from "@/hooks/useSeo";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CheckCircle2, Building2, Users, FileCheck2, Wallet } from "lucide-react";

const VOLUME_OPTIONS = ["1–20", "21–50", "51–100", "101–250", "251–500", "500+"];

const EMPTY = {
  agency_name: "",
  contact_name: "",
  email: "",
  phone: "",
  primary_service_area: "",
  assignments_per_month: "",
  current_system: "",
  services_provided: "",
  interested_in: "",
  biggest_challenge: "",
  website: "",
  num_servers: "",
  additional_info: "",
};

const CAPABILITIES = [
  { icon: FileCheck2, label: "Assignments, attempts & evidence" },
  { icon: Users, label: "Server & staff management" },
  { icon: Wallet, label: "Billing, payouts & returns" },
  { icon: Building2, label: "Client communication" },
];

export default function AgencyAccess() {
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [agr, setAgr] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const agencyNameRef = useRef(null);
  const scrollToForm = () => {
    agencyNameRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => agencyNameRef.current?.focus({ preventScroll: true }), 450);
  };

  useEffect(() => { api.get("/agency-agreement").then(({ data }) => setAgr(data)).catch(() => {}); }, []);

  useSeo({
    title: "Request Agency Access | AR Process Service",
    description:
      "Process-serving agencies: request access to the AR Process Service platform to manage assignments, servers, attempts, evidence, returns, billing, payouts, and client communication.",
    canonicalPath: "/agency-access",
  });

  const submit = async () => {
    const required = [
      "agency_name", "contact_name", "email", "phone", "primary_service_area",
      "assignments_per_month", "current_system", "services_provided", "interested_in", "biggest_challenge",
    ];
    if (required.some((k) => !String(f[k] || "").trim())) {
      toast.error("Please complete all required fields.");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email.trim())) {
      toast.error("Please enter a valid email address.");
      return;
    }
    if (!agreed) {
      toast.error("Please accept the ARPS Agency Platform Services Agreement to continue.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/agency-access", { ...f, agreement_accepted: true, agreement_signature: f.contact_name });
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (e) {
      toast.error("Could not submit your request. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <MarketingLayout>
      <PageHero
        overline="For Agencies"
        title="Request Agency Access"
        subtitle="AR Process Service gives process-serving agencies one platform to manage assignments, servers, service attempts, evidence, returns, billing, payouts, and client communication — end to end."
        extraCta={
          <Button
            type="button"
            data-testid="one-hour-demo-cta"
            onClick={scrollToForm}
            className="min-h-[48px] rounded-full bg-red-600 px-7 text-base font-semibold text-white shadow-sm transition-colors hover:bg-red-700 focus-visible:ring-2 focus-visible:ring-red-400"
          >
            14-Day System Demo
          </Button>
        }
      />

      <section className="px-5 py-12 sm:px-8 lg:py-16">
        <div className="mx-auto max-w-5xl">
          {done ? (
            <div
              data-testid="agency-access-success"
              className="mx-auto max-w-2xl rounded-3xl border border-slate-200 bg-white p-8 text-center shadow-sm sm:p-12"
            >
              <CheckCircle2 className="mx-auto h-14 w-14 text-sky-600" />
              <h2 className="mt-5 font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                Request Received
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-[15px] leading-relaxed text-slate-600 sm:text-base">
                Thank you for your interest in ARPS. We've received your information and will contact you to discuss your agency and next steps.
              </p>
            </div>
          ) : (
            <div className="grid gap-10 lg:grid-cols-[1fr_1.4fr]">
              <div>
                <h2 className="font-display text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  One platform for your agency
                </h2>
                <p className="mt-4 text-[15px] leading-relaxed text-slate-600 sm:text-base">
                  Tell us about your operation and what you're looking for. Our team will follow up to walk through how ARPS fits your workflow.
                </p>
                <ul className="mt-6 space-y-3">
                  {CAPABILITIES.map((c) => (
                    <li key={c.label} className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
                      <c.icon className="h-5 w-5 shrink-0 text-sky-700" />
                      <span className="text-[15px] font-medium text-slate-800">{c.label}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8" data-testid="agency-access-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <Label htmlFor="agency_name">Agency Name *</Label>
                    <Input ref={agencyNameRef} id="agency_name" data-testid="agency-field-agency-name" value={f.agency_name} onChange={set("agency_name")} />
                  </div>
                  <div>
                    <Label htmlFor="contact_name">Contact Name *</Label>
                    <Input id="contact_name" data-testid="agency-field-contact-name" value={f.contact_name} onChange={set("contact_name")} />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input id="email" type="email" data-testid="agency-field-email" value={f.email} onChange={set("email")} />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input id="phone" data-testid="agency-field-phone" value={f.phone} onChange={set("phone")} />
                  </div>
                  <div>
                    <Label htmlFor="primary_service_area">Primary Service Area *</Label>
                    <Input id="primary_service_area" data-testid="agency-field-service-area" value={f.primary_service_area} onChange={set("primary_service_area")} placeholder="e.g., Shelby County, TN" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label>Approximate Assignments Per Month *</Label>
                    <Select value={f.assignments_per_month} onValueChange={(v) => setF((p) => ({ ...p, assignments_per_month: v }))}>
                      <SelectTrigger data-testid="agency-field-assignments" className="mt-1">
                        <SelectValue placeholder="Select a range" />
                      </SelectTrigger>
                      <SelectContent>
                        {VOLUME_OPTIONS.map((o) => (
                          <SelectItem key={o} value={o} data-testid={`agency-assignments-option-${o}`}>{o}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="current_system">Current System / Platform *</Label>
                    <Input id="current_system" data-testid="agency-field-current-system" value={f.current_system} onChange={set("current_system")} placeholder="e.g., spreadsheets, ServeManager, none" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="services_provided">Services Provided *</Label>
                    <Input id="services_provided" data-testid="agency-field-services-provided" value={f.services_provided} onChange={set("services_provided")} placeholder="e.g., process serving, skip tracing, court filing" />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="interested_in">What Are You Interested In? *</Label>
                    <Textarea id="interested_in" rows={2} data-testid="agency-field-interested-in" value={f.interested_in} onChange={set("interested_in")} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="biggest_challenge">Biggest Challenge *</Label>
                    <Textarea id="biggest_challenge" rows={2} data-testid="agency-field-biggest-challenge" value={f.biggest_challenge} onChange={set("biggest_challenge")} />
                  </div>

                  <div className="sm:col-span-2 mt-2 border-t border-slate-200 pt-4">
                    <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Optional</p>
                  </div>
                  <div>
                    <Label htmlFor="website">Website</Label>
                    <Input id="website" data-testid="agency-field-website" value={f.website} onChange={set("website")} />
                  </div>
                  <div>
                    <Label htmlFor="num_servers">Number of Servers</Label>
                    <Input id="num_servers" data-testid="agency-field-num-servers" value={f.num_servers} onChange={set("num_servers")} />
                  </div>
                  <div className="sm:col-span-2">
                    <Label htmlFor="additional_info">Additional Information</Label>
                    <Textarea id="additional_info" rows={3} data-testid="agency-field-additional-info" value={f.additional_info} onChange={set("additional_info")} />
                  </div>
                </div>

                <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4" data-testid="agency-agreement-box">
                  <p className="text-sm font-bold text-slate-900">{agr?.title || "ARPS Agency Platform Services Agreement"}{agr?.version ? ` (${agr.version})` : ""}</p>
                  <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-slate-200 bg-white p-3 text-xs leading-relaxed text-slate-600">
                    {(agr?.sections || []).map((s, i) => (
                      <div key={i} className="mb-2">
                        <p className="font-semibold text-slate-700">{s.heading}</p>
                        {(s.paragraphs || []).map((p, j) => <p key={j} className="mt-1">{p}</p>)}
                      </div>
                    ))}
                  </div>
                  <label className="mt-3 flex items-start gap-2.5">
                    <Checkbox data-testid="agency-agreement-accept" checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" />
                    <span className="text-sm text-slate-700">I have read and agree to the ARPS Agency Platform Services Agreement on behalf of my agency.</span>
                  </label>
                </div>

                <Button
                  data-testid="agency-access-submit"
                  onClick={submit}
                  disabled={busy || !agreed}
                  className="mt-6 w-full bg-sky-600 text-white hover:bg-sky-700 sm:w-auto"
                >
                  {busy ? "Submitting…" : "Submit Request"}
                </Button>
              </div>
            </div>
          )}
        </div>
      </section>
    </MarketingLayout>
  );
}
